/**
 * Client-side mirror of the server-side report registry
 * (`apps/api/src/reports/aggregators/*.ts` → `registerReport(...)`).
 *
 * Intentional duplication: the server registry is server-only (Drizzle +
 * D1-typed aggregator functions). The client only needs the catalogue
 * metadata (slug, title, capability, grouping, filter shape, chart kind)
 * to render the sidebar + filter form. Sub-spec 08 Plan 2 §1.3.
 *
 * The /api/reports listing endpoint is the source of truth at runtime — the
 * UI fetches it and intersects with this registry by slug so any
 * server-side gating (capability filter) still wins. Slugs missing from
 * this file render as "unknown report".
 */
export type ReportSlug =
  | 'todays_brief'
  | 'revenue_by_date'
  | 'revenue_by_customer'
  | 'occupancy_boarding'
  | 'occupancy_daycare'
  | 'pos_sales'
  | 'refunds'
  | 'customer_list'
  | 'stay_duration'
  | 'vaccinations_expiring'
  | 'outstanding_balances'
  | 'deposit_liability'
  | 'tax_summary'
  // Migration 0100 — UK licensing inspection pack.
  | 'licence_register'
  | 'cleaning_log'
  // Migration 0098 — gratuities.
  | 'tips_by_date';

export type ReportCapability = 'reports.view_operational' | 'reports.view_financial';

export type ReportGroup = 'operational' | 'financial';

export type ReportChartKind = 'line' | 'bar' | 'bar_horizontal' | 'histogram' | 'none';

export type ReportFilterFieldKind = 'date_range' | 'select' | 'toggle' | 'text' | 'none';

export interface ReportFilterField {
  key: string;
  label: string;
  kind: ReportFilterFieldKind;
  options?: ReadonlyArray<{ value: string; label: string }>;
}

export interface ReportRegistryEntry {
  slug: ReportSlug;
  title: string;
  capability: ReportCapability;
  group: ReportGroup;
  /** Default chart for this report — `none` means table-only. */
  chart: ReportChartKind;
  /** Whether the report needs a date range (some are point-in-time). */
  requiresDateRange: boolean;
  /** Per-report filter fields beyond the date range. */
  filters: ReadonlyArray<ReportFilterField>;
}

export const REPORTS: readonly ReportRegistryEntry[] = [
  {
    slug: 'todays_brief',
    title: "Today's brief",
    capability: 'reports.view_operational',
    group: 'operational',
    chart: 'none',
    requiresDateRange: false,
    filters: [],
  },
  {
    slug: 'revenue_by_date',
    title: 'Revenue by date',
    capability: 'reports.view_financial',
    group: 'financial',
    chart: 'line',
    requiresDateRange: true,
    filters: [
      {
        key: 'flow',
        label: 'Flow',
        kind: 'select',
        options: [
          { value: '', label: 'All' },
          { value: 'customer_booking', label: 'Bookings' },
          { value: 'customer_pos', label: 'POS' },
        ],
      },
    ],
  },
  {
    slug: 'revenue_by_customer',
    title: 'Revenue by customer',
    capability: 'reports.view_financial',
    group: 'financial',
    chart: 'bar_horizontal',
    requiresDateRange: true,
    filters: [],
  },
  {
    slug: 'occupancy_boarding',
    title: 'Boarding occupancy',
    capability: 'reports.view_operational',
    group: 'operational',
    chart: 'line',
    requiresDateRange: true,
    filters: [],
  },
  {
    slug: 'occupancy_daycare',
    title: 'Daycare occupancy',
    capability: 'reports.view_operational',
    group: 'operational',
    chart: 'line',
    requiresDateRange: true,
    filters: [],
  },
  {
    slug: 'pos_sales',
    title: 'POS sales',
    capability: 'reports.view_financial',
    group: 'financial',
    chart: 'bar',
    requiresDateRange: true,
    filters: [
      {
        key: 'groupBy',
        label: 'Group by',
        kind: 'select',
        options: [
          { value: 'date', label: 'Date' },
          { value: 'product', label: 'Product' },
          { value: 'session', label: 'Session' },
        ],
      },
    ],
  },
  {
    slug: 'refunds',
    title: 'Refunds',
    capability: 'reports.view_financial',
    group: 'financial',
    chart: 'none',
    requiresDateRange: true,
    filters: [
      {
        key: 'status',
        label: 'Status',
        kind: 'select',
        options: [
          { value: '', label: 'All' },
          { value: 'requested', label: 'Requested' },
          { value: 'approved', label: 'Approved' },
          { value: 'denied', label: 'Denied' },
          { value: 'completed', label: 'Completed' },
        ],
      },
    ],
  },
  {
    slug: 'customer_list',
    title: 'Customer list',
    capability: 'reports.view_operational',
    group: 'operational',
    chart: 'none',
    requiresDateRange: false,
    filters: [],
  },
  {
    slug: 'stay_duration',
    title: 'Stay duration',
    capability: 'reports.view_operational',
    group: 'operational',
    chart: 'histogram',
    requiresDateRange: true,
    filters: [],
  },
  {
    slug: 'vaccinations_expiring',
    title: 'Vaccinations expiring',
    capability: 'reports.view_operational',
    group: 'operational',
    chart: 'none',
    requiresDateRange: false,
    filters: [
      {
        key: 'withinDays',
        label: 'Within (days)',
        kind: 'text',
      },
    ],
  },
  {
    slug: 'outstanding_balances',
    title: 'Outstanding balances (A/R)',
    capability: 'reports.view_financial',
    group: 'financial',
    chart: 'bar_horizontal',
    requiresDateRange: true,
    filters: [
      {
        key: 'agingBucket',
        label: 'Aging bucket',
        kind: 'select',
        options: [
          { value: '', label: 'All' },
          { value: 'not_yet_due', label: 'Not yet due' },
          { value: '0-30', label: '0–30 days overdue' },
          { value: '31-60', label: '31–60 days overdue' },
          { value: '61-90', label: '61–90 days overdue' },
          { value: '90+', label: '90+ days overdue' },
        ],
      },
    ],
  },
  {
    slug: 'deposit_liability',
    title: 'Deposit liability',
    capability: 'reports.view_financial',
    group: 'financial',
    chart: 'none',
    requiresDateRange: true,
    filters: [],
  },
  {
    slug: 'tax_summary',
    title: 'Tax / VAT summary',
    capability: 'reports.view_financial',
    group: 'financial',
    chart: 'bar',
    requiresDateRange: true,
    filters: [
      {
        key: 'groupBy',
        label: 'Group by',
        kind: 'select',
        options: [
          { value: 'month', label: 'Month' },
          { value: 'source', label: 'Source (Bookings / POS)' },
        ],
      },
    ],
  },
  // Migration 0100 — UK licensing inspection pack.
  {
    slug: 'licence_register',
    title: 'Licence register',
    capability: 'reports.view_operational',
    group: 'operational',
    chart: 'none',
    requiresDateRange: false,
    filters: [],
  },
  {
    slug: 'cleaning_log',
    title: 'Cleaning log',
    capability: 'reports.view_operational',
    group: 'operational',
    chart: 'none',
    requiresDateRange: false,
    filters: [],
  },
  {
    slug: 'tips_by_date',
    title: 'Tips by date',
    capability: 'reports.view_financial',
    group: 'financial',
    chart: 'bar',
    requiresDateRange: true,
    filters: [],
  },
];

export function getReportEntry(slug: string): ReportRegistryEntry | undefined {
  return REPORTS.find((r) => r.slug === slug);
}

export function reportsByGroup(group: ReportGroup): ReportRegistryEntry[] {
  return REPORTS.filter((r) => r.group === group);
}
